import { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Modal, ModalProps, SelectField } from '@speeki/global-ui-components';
import { notify } from '@speeki/global-ui-components';
import { ModalComponent } from '@speeki/global-ui-components';
import { Button } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import {
    UpdateSettingsDocument,
    UserAuthLanguage,
} from '@graphql/generated/graphql';

import { currentLanguageSelect, languagesOptions } from '@utils/dictionary';

import { useMutation, useReactiveVar } from '@apollo/client';
import { translationsStatusVar } from '@boot/reactiveVariables';
import { yupResolver } from '@hookform/resolvers/yup';
import { requiredSelectV2 } from '@shared/validations';

import { DescriptionWrapper } from './LanguageChangeModal.styled';

interface LanguageChangeModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {}

export const LanguageChangeModal = ({
    onClose,
    open,
}: LanguageChangeModalProps) => {
    const { t } = useTranslation();
    const translationsStatus = useReactiveVar(translationsStatusVar);

    const {
        control,
        formState: { errors, isDirty, isSubmitting },
        handleSubmit,
        reset,
    } = useForm<ChangeLanguageFormValues>({
        defaultValues: {
            language: currentLanguageSelect(),
        },
        mode: 'onChange',
        resolver: yupResolver(ChangeLanguageSchema),
    });

    const [updateSettings, { data, loading: updateLoading }] = useMutation(
        UpdateSettingsDocument,
        {
            context: {
                replaceTokens: true,
            },
            onCompleted: () => {
                // NOTE: setTimeout is needed to show the success notification in selected language
                setTimeout(
                    () => notify(t('CHANGE_LANGUAGE_SUCCESS')).SUCCESS(),
                    100,
                );
            },
            onError: () => {},
        },
    );

    const loading = updateLoading || translationsStatus === 'loading';
    const success = translationsStatus === 'success' && !!data;

    const handleLanguageChange = (data: ChangeLanguageFormValues) => {
        void updateSettings({
            variables: {
                input: { language: data.language.value as UserAuthLanguage },
            },
        });
    };

    const onCancel = () => {
        reset();
        onClose();
    };

    const { field: languageSelect } = useController({
        control,
        name: 'language',
    });

    useEffect(() => {
        if (success) onClose();
    }, [success]);

    return (
        <ModalComponent closeButton onClose={onClose} open={open} width={648}>
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('CHANGE_LANGUAGE')}
                    </Modal.TitleSecondary>
                    <DescriptionWrapper>
                        <Modal.Description>
                            {t('CHANGE_LANGUAGE_INFO')}
                        </Modal.Description>
                        <Modal.Description>
                            {t('SETTINGS_CHANGE_LANGUAGE_TEXT')}
                        </Modal.Description>
                    </DescriptionWrapper>
                </Modal.Header>

                <StandardForm>
                    <SelectField
                        {...languageSelect}
                        error={!!errors.language}
                        label={t('LANGUAGE')}
                        message={t(errors.language?.message || '')}
                        options={languagesOptions}
                        placeholder={t('LANGUAGE_PLACEHOLDER')}
                        testId="select_change-language"
                    />
                </StandardForm>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={onCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={!isDirty || isSubmitting || loading}
                        isLoading={isSubmitting || loading}
                        onClick={handleSubmit(handleLanguageChange)}
                        testId="button_save"
                        variant={'primary'}
                    >
                        {t('SAVE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const ChangeLanguageSchema = yup.object({
    language: requiredSelectV2,
});

type ChangeLanguageFormValues = yup.InferType<typeof ChangeLanguageSchema>;
